import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { GenerateRuleStep } from './Generate';
import { Code, Type } from '@/shared/schema';
import { SaveRuleStep } from './SaveRule';
import { ConfirmationDialog } from '@/widgets/AlertCommon';
import { useBreadcrumbs } from '../BreadcrumbContext';

type Step = 'generate' | 'map' | 'test' | 'save';
type TokenType = 'datatype-attribute' | 'operand' | 'logical-operator';

export interface VariablePair {
    id: string;
    type: TokenType;
    dataType?: string;
    attribute?: string;
    placeholder: string;
    mappedDataType?: string | null;
    mappedAttribute?: string | null;
    operand?: string;
    mappedOperand?: string | null;
    logicalOperator?: string;
    mappedLogicalOperator?: string | null;
}

export interface MappedPair {
    dataType: string | null;
    attribute: string | null;
}

export default function CreateRulePrompt() {
    const [currentStep, setCurrentStep] = useState<Step>('generate');
    const [generatedRule, setGeneratedRule] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);

    const [variablePairs, setVariablePairs] = useState<VariablePair[]>([]);
    const [currentPairIndex, setCurrentPairIndex] = useState(0);

    const [testResult, setTestResult] = useState<{ success?: boolean; message: string; details: string }>({ message: "", details: "" });
    const [isSaving, setIsSaving] = useState(false);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");

    useEffect(() => {
        const saved = sessionStorage.getItem("generatedRule");
        if (saved) setGeneratedRule(saved);
    }, []);

    const { setBreadcrumbs } = useBreadcrumbs();

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: `AI Console`,
                path: `/ai-builder`,
            },
        ]);
    }, []);

    useEffect(() => {
        if (generatedRule && currentStep === 'map') {
            const pairs: VariablePair[] = [];
            let id = 0;

            const dtAttrRegex = /#([^#]+)#\.\[([^\]]+)\]/g;
            let match;
            while ((match = dtAttrRegex.exec(generatedRule)) !== null) {
                pairs.push({
                    id: `pair-${id++}`,
                    type: 'datatype-attribute',
                    dataType: match[1],
                    attribute: match[2],
                    placeholder: match[0],
                    mappedDataType: null,
                    mappedAttribute: null
                });
            }

            const operandRegex = /@([^@]+)@/g;
            while ((match = operandRegex.exec(generatedRule)) !== null) {
                pairs.push({
                    id: `pair-${id++}`,
                    type: 'operand',
                    operand: match[1],
                    placeholder: match[0],
                    mappedOperand: null
                });
            }

            const logicalRegex = /\$([^$]+)\$/g;
            while ((match = logicalRegex.exec(generatedRule)) !== null) {
                pairs.push({
                    id: `pair-${id++}`,
                    type: 'logical-operator',
                    logicalOperator: match[1],
                    placeholder: match[0],
                    mappedLogicalOperator: null
                });
            }

            pairs.sort((a, b) => {
                return generatedRule.indexOf(a.placeholder) - generatedRule.indexOf(b.placeholder);
            });

            setVariablePairs(pairs);
        }
    }, [generatedRule, currentStep]);

    const getFinalRule = () => {
        if (!generatedRule) return "";

        let final = generatedRule;
        variablePairs.forEach((pair) => {
            if (pair.type === 'datatype-attribute' && pair.mappedDataType && pair.mappedAttribute) {
                final = final.replace(pair.placeholder, `${pair.mappedDataType}.${pair.mappedAttribute}`);
            } else if (pair.type === 'operand' && pair.mappedOperand) {
                final = final.replace(pair.placeholder, pair.mappedOperand);
            } else if (pair.type === 'logical-operator' && pair.mappedLogicalOperator) {
                final = final.replace(pair.placeholder, pair.mappedLogicalOperator);
            }
        });
        return final;
    };

    const updateVariablePair = (index: number, updates: Partial<VariablePair>) => {
        setVariablePairs(prev => prev.map((pair, i) =>
            i === index ? { ...pair, ...updates } : pair
        ));
    };

    const allPairsCompleted = variablePairs.every(pair => {
        if (pair.type === 'datatype-attribute') {
            return pair.mappedDataType && pair.mappedAttribute;
        } else if (pair.type === 'operand') {
            return pair.mappedOperand;
        } else if (pair.type === 'logical-operator') {
            return pair.mappedLogicalOperator;
        }
        return false;
    });

    const handleSaveRule = async (data: any) => {
        setIsSaving(true);
    };

    const handleStartOver = () => {
        setCurrentStep('generate');
        sessionStorage.removeItem("rule_chat_history");
        sessionStorage.removeItem("rule_session_id");
        sessionStorage.removeItem("generatedRule");
        localStorage.removeItem("selectedRuleAI");
        localStorage.removeItem("selectedRule");
        localStorage.removeItem("selectedVersionRule");
        setGeneratedRule(null);
        setVariablePairs([]);
        setCurrentPairIndex(0);
        setTestResult({ message: "", details: "" });
        setShowResetDialog(false);
    };

    const stepConfig = {
        generate: { title: 'Generate Form', number: 1 },
    };

    const closeSave = () => {
        handleStartOver();
        setAlertOpen(false)
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="space-y-6">
                <div className="text-left space-y-2">
                    <h1 className="text-xl md:text-xl font-semibold text-gray-900">AI Workflow Builder</h1>
                    <p className="text-muted-foreground">Create Workflow with natural language</p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 md:gap-6">
                    <div className="xl:col-span-4 space-y-6">

                        <div className="min-h-[400px]">
                            {currentStep === 'generate' && (
                                <GenerateRuleStep
                                    onNext={() => setCurrentStep('map')}
                                    generatedRule={generatedRule}
                                    setGeneratedRule={setGeneratedRule}
                                    isGenerating={isGenerating}
                                    setIsGenerating={setIsGenerating}
                                />
                            )}

                            {currentStep === 'save' && (
                                <SaveRuleStep
                                    onBack={() => setCurrentStep('test')}
                                    finalRule={getFinalRule()}
                                    onSave={handleSaveRule}
                                    isSaving={isSaving}
                                />
                            )}


                        </div>
                        {currentStep !== 'generate' && (
                            <div className="flex justify-end">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowResetDialog(true)}
                                    className="text-muted-foreground"
                                >
                                    Start Over
                                </Button>
                            </div>
                        )}
                    </div>

                </div>

                <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Start Over?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will discard all your current progress and reset the wizard. Are you sure?
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleStartOver}>
                                Yes, Start Over
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            <ConfirmationDialog
                open={alertOpen}
                onOpenChange={() => setAlertOpen(false)}
                onConfirm={closeSave}
                message={alertMessage}
                // heading={createAIRuleMutation.isSuccess === true ? "Success" : "Save Rule Failed"}
                heading={"Save Rule Failed"}
            />
        </div>
    );
}